import {
  SimpleTextAttachmentAdapter,
  type Attachment,
  type CompleteAttachment,
  type PendingAttachment,
} from '@assistant-ui/react';
import { randomUuid } from './random-uuid.ts';

// Character threshold, independent of clipboard markup or content. The existing
// inline byte limit still applies before the adapter reads/encodes the paste.
export const LARGE_PASTE_CHARACTERS = 2_000;
const PASTE_URL = 'data:text/plain;source=paste;base64,';

export class PastedTextFile extends File {
  readonly textContent: string;
  readonly attachmentId: string;
  constructor(text: string, name = 'Pasted text.txt', id = randomUuid()) {
    super([text], name, { type: 'text/plain' });
    this.textContent = text;
    this.attachmentId = id;
  }
}

export function largeClipboardText(
  clipboard: Pick<DataTransfer, 'files' | 'getData'>,
): string | null {
  if (clipboard.files.length) return null;
  const text = clipboard.getData('text/plain');
  return text.length > LARGE_PASTE_CHARACTERS ? text : null;
}

function encodeText(text: string) {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192)
    binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
  return PASTE_URL + btoa(binary);
}

export function isPastedText(attachment: Attachment): boolean {
  return (
    attachment.file instanceof PastedTextFile ||
    Boolean(
      attachment.content?.some((part) => part.type === 'file' && part.data.startsWith(PASTE_URL)),
    )
  );
}

export function pastedText(attachment: Attachment): string | null {
  if (attachment.file instanceof PastedTextFile) return attachment.file.textContent;
  const part = attachment.content?.find(
    (part) => part.type === 'file' && part.data.startsWith(PASTE_URL),
  );
  if (part?.type !== 'file') return null;
  try {
    return new TextDecoder('utf-8', { ignoreBOM: true }).decode(
      Uint8Array.from(atob(part.data.slice(PASTE_URL.length)), (c) => c.charCodeAt(0)),
    );
  } catch {
    return null;
  }
}

export function pastePreview(text: string) {
  const end = text.indexOf('\n');
  return text.slice(0, end < 0 ? 100 : Math.min(end, 100)).trim();
}

// Textareas expose LF-normalized selection offsets even when the draft still
// contains the original CRLF bytes from an earlier restored paste.
function sourceOffset(text: string, offset: number) {
  let source = 0;
  for (let displayed = 0; displayed < offset && source < text.length; displayed++) {
    if (text[source] === '\r' && text[source + 1] === '\n') source++;
    source++;
  }
  return source;
}

export function restorePastedText(text: string, start: number, end: number, paste: string) {
  const at = sourceOffset(text, Math.max(0, start));
  const to = sourceOffset(text, Math.max(start, end));
  return { text: text.slice(0, at) + paste + text.slice(to), caret: at + paste.length };
}

// Ordinary file uploads retain their existing adapter. Pastes use an inline
// text file so history retains a chip; the server's inlineTextFileToText feeds
// every byte to the model using its existing provider-neutral text path.
export class PastedTextAttachmentAdapter extends SimpleTextAttachmentAdapter {
  async add({ file }: { file: File }): Promise<PendingAttachment> {
    if (!(file instanceof PastedTextFile)) return super.add({ file });
    return {
      id: file.attachmentId,
      type: 'document',
      name: file.name,
      contentType: 'text/plain',
      file,
      status: { type: 'requires-action', reason: 'composer-send' },
      content: [
        {
          type: 'file',
          data: encodeText(file.textContent),
          mimeType: 'text/plain',
          filename: file.name,
        },
      ],
    };
  }
  async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
    if (!(attachment.file instanceof PastedTextFile)) return super.send(attachment);
    return { ...attachment, status: { type: 'complete' }, content: attachment.content! };
  }
}
